<template>

  <div class="dashboard-block">

    <h2 v-if="state === 'completed'" >Upload Complete</h2>
    <h2 v-if="state === 'open'" >Upload a Video</h2>
    <h2 v-if="state === 'preview' || state === 'uploading'" >Confirm Your Video</h2>

    <p v-if="state === 'open'" >Upload your video below. You will be able to preview your video when the upload completes.</p>
    <p v-if="state === 'preview' || state === 'uploading'" >Complete your booking when you are satisfied with the uploaded video.</p>

    <div class="videoUpload_video" v-if="state === 'completed'" >

      <iframe v-if="booking.video_link" :src="booking.video_link" frameborder="0" ></iframe>

      <div v-else class="videoUpload_processing" >
        <h3>We're Processing Your Video</h3>
        <p>Your video will be processed and sent to the client directly.</p>
      </div>

    </div>

    <div v-else >

      <input class="videoUpload_fileInput" ref="fileInput" type="file" accept="video/*"  @change="fileSelected">

      <div class="videoUpload_dropzone m-t-lg" @drop="fileDropped" @dragenter="dropable = true" @dragleave="dropable = false"  >

        <div v-if="state === 'open'" class="videoUpload_prompt" :class="{ 'is-droppable' : dropable }" >
          <i class="fa fa-cloud-upload fa-3x"></i>
          <h3 class="text-upper" >Drag and drop file</h3>
          <div class="text-upper text-grey" >Or</div>
          <button
            type="button"
            class="btn btn-primary" @click="chooseFile" >
            Choose File
          </button>
        </div>

        <div v-show="state === 'preview' || state === 'uploading'" class="videoUpload_preview" >
          <video ref="player" class="videoUpload_preview_player" controls ></video>
        </div>

        <div class="videoUpload_uploading" v-if="state === 'uploading'" >
          <h3>Uploading... {{ uploadPercentage  }}%</h3>
          <p>Please don't leave the page</p>
        </div>

      </div>

      <div class="m-t-lg flex justify-between">
        <button
          v-if="state === 'preview'"
          type="button"
          class="btn btn-secondary" @click="chooseFile"  >
          Replace Video
        </button>
        <button
          type="button"
          class="btn btn-primary ml-auto"
          :disabled="state !== 'preview'"
          @click="completeBooking"
        >
          Complete Booking
        </button>
      </div>

    </div>

  </div>

</template>

<script>

import { notify } from 'utils'
import {mapActions} from 'vuex'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [OpportunityTracker],

  props: {
    booking: {
      required: true,
      type: Object
    },
    talent: {
      required: true,
      type: Object
    }
  },

  data () {
    return {
      state: this.booking.status === 'completed' ? 'completed' : 'open',
      file: null,
      reader: null,
      dropable: false,
      processing: false,
      uploadPercentage: 0
    }
  },

  methods: {

    ...mapActions('booking', ['markBookingComplete']),

    chooseFile () {
      this.$refs.fileInput.click()
    },

    fileSelected (e) {
      if (!e.target.files || !e.target.files.length) {
        return
      }

      this.setFile(e.target.files[0])
    },

    fileDropped (e) {
      this.dropable = false

      if (!e.dataTransfer.files || !e.dataTransfer.files.length) {
        return
      }

      this.setFile(e.dataTransfer.files[0])
    },

    setFile (file) {
      if (![ 'open', 'preview' ].includes(this.state)) { return }

      if (!file.type.match('video/*')) {
        notify.error('The file must be a video')
        return
      }

      this.state = 'preview'
      this.file = file
      this.loadPreview(file)
    },

    loadPreview (file) {
      const reader = new FileReader()
      const player = this.$refs.player

      reader.addEventListener('load', function () {
        player.src = reader.result
        player.load()
      }, false)

      reader.readAsDataURL(file)
    },

    async completeBooking () {
      this.uploadPercentage = 0
      this.state = 'uploading'

      try {
        this.trackEvent(this.analytics.V2.JobVideoUploadEvent)
        const s3Url = await this.uploadFile(this.file)
        await this.finaliseUpload(s3Url)
        await this.markBookingComplete({id: this.booking.id, type: 'online', userId: this.booking.talent[0].id})
        this.trackEvent(this.analytics.V2.JobBookingCompleteEvent)
        this.state = 'completed'
      } catch (e) {
        notify.error('An error occurred. Please try again.')
        this.state = 'preview'
      }
    },

    async uploadFile (file) {
      const response = await this.$http.get('uploads/pre-sign', {
        params: {
          name: file.name,
          type: file.type
        }
      })

      const signedUrl = response.data.uri

      await this.$http.put(signedUrl, file, {
        headers: {
          'Content-Type': file.type
        },
        withCredentials: false,
        onUploadProgress: e => {
          this.uploadPercentage = Math.floor((e.loaded / e.total) * 100)
        }
      })
      // fix janky s3 file structure. good luck!
      // eslint-disable-next-line compat/compat
      let parsedUrl = new URL(signedUrl)
      let s3 = parsedUrl.origin + '/' + encodeURIComponent(parsedUrl.pathname.substring(1))

      return s3
    },

    async finaliseUpload (s3url) {
      await this.$http.put(`packages/process/${this.booking.id}`, { s3_url: s3url })
    }

  }

}

</script>

<style lang="scss" >

  .videoUpload_fileInput {
    display: none !important;
  }

  .videoUpload_preview {
    background: black;
    padding-bottom: 9/16 * 100%;
    position: relative;
    overflow: hidden;
  }

  .videoUpload_dropzone {
    height: 0;
    padding-bottom: 9/16 * 100%;
    position: relative;
  }

  .videoUpload_prompt {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    border: dashed 2px #bdbdbd;

    &.is-droppable {
      background-color: #bdbdbd;
    }

    > *:not(:last-child) {
      margin-bottom: 10px;
    }
  }

  .videoUpload_preview_player {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    outline: none;
  }

  .videoUpload_uploading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: rgba( #000, 0.8 );
    color: white;
  }

  .videoUpload_video {
    height: 0;
    padding-bottom: 9/16 * 100%;
    position: relative;
    background-color: black;
    color: white;

    > * {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .videoUpload_processing {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 3rem;
  }

</style>
